/* Read-only enhancement for Shopify's native shipping policy. No cart writes,
 * checkout redirects, address persistence, or tracking. Rates come from the
 * deployed quote endpoint; selected service amounts come from the current cart. */
(() => {
  'use strict';
  const root = document.querySelector('.wnf-shipping-policy');
  if (!root) return;
  // Shopify strips details/summary from stored policy output. Start with visible
  // headings and text, then add native disclosures only in the storefront.
  root.querySelectorAll('.wnf-policy-detail-source').forEach(source => {
    const title = source.querySelector(':scope > h3');
    const content = source.querySelector(':scope > div');
    if (!title || !content) return;
    const details = document.createElement('details');
    details.className = 'wnf-policy-detail';
    const summary = document.createElement('summary');
    summary.textContent = title.textContent;
    details.append(summary, content);
    source.replaceWith(details);
  });
  const mount = root && root.querySelector('#wnf-policy-quote-ui');
  if (!mount || mount.dataset.mounted === 'true') return;

  const endpoint = 'https://wnf-delivery.vercel.app/api/quote';
  const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  const transportSKUs = new Set(['DELIV-LOC-149', 'DELIV-LOC-FREE', 'DELIV-MILE-2', 'DELIV-WA-149', 'DELIV-OR-FREE', 'DELIV-FRT-QTE']);
  // Separate Art Prints shipping profile, verified 2026-09-06. Preflight must
  // compare membership with evidence/rates/art-print-profile-products.json.
  const separateShippingProducts = new Set(['8786397954206', '8819844186270', '8819850510494', '8819875709086', '8820886077598']);
  const phone = root.dataset.contactPhone || '+15032353454';
  const phoneDisplay = root.dataset.contactDisplay || '(503) 235-3454';
  let version = 0;
  let activeController = null;

  function element(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  }
  function contactLink() {
    const link = element('a', '', `Call ${phoneDisplay}`);
    link.href = `tel:${phone}`;
    return link;
  }
  function cartLink() {
    const link = element('a', '', 'Review your current cart');
    link.href = '/cart';
    return link;
  }
  function appendLinks(target) {
    const links = element('p', 'wnf-quote-actions');
    links.append(cartLink(), document.createTextNode(' · '), contactLink());
    target.append(links);
  }
  const form = element('form', 'wnf-quote-form');
  form.setAttribute('aria-label', 'Estimate front-door delivery');
  form.setAttribute('aria-describedby', 'wnf-quote-input-note');
  const note = element('p', '', 'Use your full delivery address. We’ll check the eligible merchandise in your current cart; services are listed separately.');
  note.id = 'wnf-quote-input-note';
  const grid = element('div', 'wnf-quote-grid');
  function field(name, labelText, autocomplete, attributes = {}) {
    const wrapper = element('div', `wnf-quote-field wnf-quote-field--${name}`);
    const label = element('label', '', labelText);
    const input = element('input');
    input.id = `wnf-quote-${name}`;
    input.name = name;
    input.type = 'text';
    input.required = true;
    input.autocomplete = autocomplete;
    label.htmlFor = input.id;
    Object.entries(attributes).forEach(([key, value]) => input.setAttribute(key, value));
    wrapper.append(label, input);
    grid.append(wrapper);
    return input;
  }
  const street = field('street', 'Street address', 'address-line1', { maxlength: '180' });
  const city = field('city', 'City', 'address-level2', { maxlength: '100' });
  const stateWrapper = element('div', 'wnf-quote-field wnf-quote-field--state');
  const stateLabel = element('label', '', 'State');
  const state = element('select');
  state.id = 'wnf-quote-state';
  state.name = 'state';
  state.required = true;
  state.autocomplete = 'address-level1';
  stateLabel.htmlFor = state.id;
  [['', 'Select state'], ['OR', 'Oregon'], ['WA', 'Washington'], ['OTHER', 'Other U.S. state']].forEach(([value, text]) => {
    const option = element('option', '', text);
    option.value = value;
    state.append(option);
  });
  stateWrapper.append(stateLabel, state);
  grid.append(stateWrapper);
  const zip = field('zip', 'ZIP code', 'postal-code', { inputmode: 'numeric', pattern: '[0-9]{5}(-[0-9]{4})?', maxlength: '10', title: 'Enter a five-digit ZIP code, optionally followed by a four-digit extension.' });
  const otherState = field('other-state', 'State name or abbreviation', 'off', { maxlength: '60' });
  otherState.required = false;
  otherState.disabled = true;
  otherState.parentElement.hidden = true;
  const submit = element('button', 'wnf-quote-submit', 'Get delivery estimate');
  submit.type = 'submit';
  const actions = element('div', 'wnf-quote-actions');
  actions.append(submit);
  form.append(note, grid, actions);
  const status = element('p', 'wnf-quote-status');
  status.id = 'wnf-quote-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  status.tabIndex = -1;
  const result = element('div', 'wnf-quote-result');
  result.hidden = true;
  result.setAttribute('aria-labelledby', 'wnf-quote-result-heading');
  result.tabIndex = -1;
  mount.append(form, status, result);
  mount.dataset.mounted = 'true';
  const fallback = root.querySelector('.wnf-policy-quote-fallback');
  if (fallback) fallback.hidden = true;

  function busy(value) {
    form.setAttribute('aria-busy', String(value));
    submit.disabled = value;
    submit.textContent = value ? 'Checking your delivery…' : 'Get delivery estimate';
  }
  function invalidate() {
    version += 1;
    if (activeController) activeController.abort();
    activeController = null;
    busy(false);
    result.hidden = true;
    status.textContent = 'Address changed. Get a new estimate for this address.';
  }
  form.addEventListener('input', invalidate);
  state.addEventListener('change', () => {
    const isOther = state.value === 'OTHER';
    otherState.parentElement.hidden = !isOther;
    otherState.disabled = !isOther;
    otherState.required = isOther;
    invalidate();
  });

  async function readJSON(url, controller, isCart) {
    const response = await fetch(url, {
      method: 'GET',
      credentials: isCart ? 'same-origin' : 'omit',
      cache: 'no-store',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
      referrerPolicy: 'strict-origin-when-cross-origin'
    });
    if (!response.ok) throw new Error('unavailable');
    return response.json();
  }
  function serviceCatalog(addOns) {
    const catalog = new Map();
    function visit(value) {
      if (!value || typeof value !== 'object') return;
      if (typeof value.sku === 'string' && Number.isFinite(value.price)) catalog.set(value.sku, value);
      Object.values(value).forEach(child => { if (child && typeof child === 'object') visit(child); });
    }
    visit(addOns);
    return catalog;
  }
  function summaryRow(list, label, value) {
    list.append(element('dt', '', label), element('dd', '', value));
  }
  function warn(message) {
    result.append(element('p', 'wnf-quote-warning', message));
  }
  function showLineList(title, lines, className) {
    result.append(element('h4', '', title));
    const list = element('ul', className);
    lines.forEach(item => {
      list.append(element('li', '', `${item.title || item.product_title || item.sku} × ${item.quantity} — ${money(item.final_line_price / 100)}`));
    });
    result.append(list);
  }
  function renderQuote(data, cart, subtotalCents) {
    const quote = data.quote;
    if (data.ok !== true || !quote || typeof quote.freight !== 'boolean' || !Number.isFinite(quote.total) || quote.total < 0 || !Number.isFinite(data.subtotal) || Math.round(data.subtotal * 100) !== subtotalCents || !data.address || typeof data.address.matched !== 'string' || !data.address.matched.trim()) throw new Error('unavailable');
    const catalog = serviceCatalog(data.addOns);
    const services = [];
    const transport = [];
    const sameDay = [];
    const unknown = [];
    const priceConflicts = [];
    cart.items.filter(item => String(item.sku || '').toUpperCase().startsWith('DELIV-')).forEach(item => {
      const sku = String(item.sku).toUpperCase();
      const current = catalog.get(sku);
      if (sku === 'DELIV-SAME-299') sameDay.push(item);
      else if (transportSKUs.has(sku)) transport.push(item);
      else if (current && !sku.startsWith('DELIV-CONSIGN-')) services.push(item);
      else unknown.push(item);
      if (current && Math.round(current.price * 100) * item.quantity !== item.final_line_price) priceConflicts.push(item);
    });
    result.replaceChildren();
    const heading = element('h3', '', quote.freight ? 'Let’s arrange a freight quote' : 'Your front-door delivery estimate');
    heading.id = 'wnf-quote-result-heading';
    result.append(heading);
    const summary = element('dl', 'wnf-quote-summary');
    summaryRow(summary, 'Matched delivery address', data.address.matched);
    summaryRow(summary, 'Eligible merchandise in this cart', money(subtotalCents / 100));
    if (!quote.freight) summaryRow(summary, 'Standard front-door delivery, per order', money(quote.total));
    result.append(summary);
    const discounted = cart.total_discount > 0 || cart.cart_level_discount_applications?.length > 0 || cart.items.some(item => item.original_line_price > item.final_line_price || item.line_level_discount_allocations?.length > 0);
    result.append(element('p', '', 'The merchandise amount uses current shippable merchandise line totals, including line discounts and excluding delivery services. Cart-level or checkout discounts may change eligibility.'));
    if (discounted) warn('Your cart includes a discount. The quote and checkout currently handle some discounts differently, so call us to confirm free-delivery eligibility and the delivery charge before ordering.');
    if (data.address.state === 'OR' && subtotalCents > 149900 && subtotalCents < 150000) warn('For orders from $1,499.01 to $1,499.99, call before ordering so we can confirm checkout includes the correct delivery charge. The amount above is an estimate from our delivery rate card.');
    if (data.address.state !== (state.value === 'OTHER' ? data.address.state : state.value) || data.address.zip?.slice(0, 5) !== zip.value.slice(0, 5)) {
      warn('The matched state or ZIP differs from your entry. Check the full matched address before relying on this estimate, or call us for help.');
    }
    if (quote.freight) {
      warn('This address needs a custom freight quote. Call us with your address, furniture and access details before ordering. ');
    } else {
      result.append(element('p', '', 'Check that the matched address is yours. This is the standard front-door delivery estimate for the eligible merchandise shown above. White glove and other services are separate.'));
    }
    const serviceCents = services.reduce((sum, item) => sum + item.final_line_price, 0);
    if (services.length) {
      showLineList('Services already selected in your cart', services, 'wnf-quote-service-list');
      result.append(element('p', '', `Selected service lines: ${money(serviceCents / 100)}. These are their current cart amounts, including line discounts.`));
    } else {
      result.append(element('p', '', 'No white-glove or other delivery service add-ons are included in this estimate.'));
    }
    const bundlePrice = catalog.get('DELIV-WG-ADD')?.price;
    const bigWhiteGlovePieces = services.reduce((count, line) => {
      const sku = String(line.sku).toUpperCase();
      return count + (sku.startsWith('DELIV-WG-') && Number.isFinite(bundlePrice) && catalog.get(sku)?.price >= bundlePrice ? line.quantity : 0);
    }, 0);
    if (bigWhiteGlovePieces >= 2) warn(`You have white glove selected for more than one large piece. Call us to confirm any eligible ${money(bundlePrice)} additional-piece bundle pricing; the cart does not apply that bundle automatically. The service amounts above are what is currently selected.`);
    if (sameDay.length) {
      showLineList('Same-day delivery selected', sameDay, 'wnf-quote-detail-list');
      const price = catalog.get('DELIV-SAME-299')?.price;
      warn(`Same-day delivery needs availability, timing and charge confirmation.${Number.isFinite(price) ? ` The current listed same-day service price is ${money(price)}.` : ''} Call us to confirm how it applies to this order; it has not been added to the standard estimate here.`);
    }
    if (transport.length) {
      showLineList('Delivery lines already in your cart', transport, 'wnf-quote-detail-list');
      const transportCents = transport.reduce((sum, item) => sum + item.final_line_price, 0);
      result.append(element('p', '', `Existing delivery lines total ${money(transportCents / 100)}. They are shown for comparison and are not added again to this estimate.`));
      if (!quote.freight && transportCents !== Math.round(quote.total * 100)) {
        warn('Your existing cart delivery lines differ from this address estimate. Call us to confirm the delivery charge before ordering.');
      }
    }
    if (unknown.length) {
      showLineList('Delivery or pickup lines that need confirmation', unknown, 'wnf-quote-detail-list');
      warn('We can’t confirm how these delivery or pickup lines apply to this order. Call us before ordering; these amounts are excluded from the estimate below.');
    }
    if (priceConflicts.length) {
      warn('Some service prices in your cart differ from the current delivery service catalog. This may reflect a discount or an older price. Call us to confirm those charges before ordering.');
    }
    if (!quote.freight && !sameDay.length && !unknown.length && !priceConflicts.length && !discounted && bigWhiteGlovePieces < 2) {
      result.append(element('p', 'wnf-quote-total', `Front-door estimate + selected services: ${money(quote.total + serviceCents / 100)}.`));
    }
    warn('This does not confirm stock, scheduling or the final checkout charge. Extra services or access needs can change the amount. If checkout shows a different delivery amount, call us to confirm before paying.');
    appendLinks(result);
    result.hidden = false;
    status.textContent = quote.freight ? 'A custom freight quote is needed.' : 'Your delivery estimate is ready. Review the matched address and separate services.';
    result.focus();
  }

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const requestVersion = ++version;
    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    busy(true);
    result.hidden = true;
    status.textContent = 'Checking your current cart and delivery address…';
    try {
      const cart = await readJSON('/cart.js', controller, true);
      if (requestVersion !== version) return;
      if (!Array.isArray(cart.items) || (cart.currency && cart.currency !== 'USD')) throw new Error('cart_unavailable');
      if (cart.items.some(item => !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0 || typeof item.requires_shipping !== 'boolean')) throw new Error('cart_unavailable');
      const merchandise = cart.items.filter(item => item.requires_shipping && !String(item.sku || '').toUpperCase().startsWith('DELIV-'));
      if (merchandise.some(item => separateShippingProducts.has(String(item.product_id)))) {
        result.replaceChildren();
        const heading = element('h3', '', 'Your art print has separate shipping');
        heading.id = 'wnf-quote-result-heading';
        result.append(heading, element('p', '', 'Your cart includes an art print with a separate shipping method. Call us to confirm shipping for this cart, especially if it also includes furniture. A standard furniture-delivery estimate would not account for that shipping method.'));
        const existingDelivery = cart.items.filter(item => String(item.sku || '').toUpperCase().startsWith('DELIV-'));
        if (existingDelivery.length) showLineList('Delivery and service lines already in your cart', existingDelivery, 'wnf-quote-detail-list');
        appendLinks(result);
        result.hidden = false;
        status.textContent = 'Separate shipping applies. Call us to confirm shipping for this cart.';
        result.focus();
        return;
      }
      if (!merchandise.length) {
        result.replaceChildren();
        const heading = element('h3', '', 'Add your furniture to the cart first');
        heading.id = 'wnf-quote-result-heading';
        result.append(heading, element('p', '', 'An accurate estimate needs the furniture you plan to order as well as your delivery address. Your cart has no eligible merchandise to quote yet. You can also call us with the pieces you have in mind.'));
        appendLinks(result);
        result.hidden = false;
        status.textContent = 'Your cart has no eligible merchandise to quote yet.';
        result.focus();
        return;
      }
      const subtotalCents = merchandise.reduce((sum, item) => sum + item.final_line_price, 0);
      if (!Number.isSafeInteger(subtotalCents)) throw new Error('cart_unavailable');
      const enteredState = state.value === 'OTHER' ? otherState.value.trim() : state.value;
      const address = `${street.value.trim()}, ${city.value.trim()}, ${enteredState} ${zip.value.trim()}`;
      const query = new URLSearchParams({ address, subtotal: (subtotalCents / 100).toFixed(2), channel: 'web' });
      const data = await readJSON(`${endpoint}?${query}`, controller, false);
      if (requestVersion !== version) return;
      if (data.error === 'address_not_found' || data.error === 'no_address') throw new Error('address_not_found');
      renderQuote(data, cart, subtotalCents);
    } catch (error) {
      if (requestVersion !== version) return;
      result.hidden = true;
      status.textContent = error.message === 'address_not_found'
        ? 'We couldn’t match that delivery address. Check the street, city, state and ZIP, then try again, or call us for a quote.'
        : 'We couldn’t complete the estimate. Please try again, or call us with your address and furniture. Your cart hasn’t been changed.';
      status.append(document.createTextNode(' '), contactLink());
      status.focus();
    } finally {
      clearTimeout(timeout);
      if (requestVersion === version) {
        activeController = null;
        busy(false);
      }
    }
  });
})();
